import { useRef } from 'react';

import { MoreHorizontal } from 'lucide-react';

import {
  IdeaAction,
  IdeaSummary,
  allowedIdeaActions,
  ideaActionLabels,
} from '@/types/service/ideaExchange';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Props {
  request: IdeaSummary;
  onSelect: (selection: {
    action: IdeaAction['action'] | 'view';
    returnFocus: HTMLElement;
  }) => void;
}

export function IdeaRowActions({ request, onSelect }: Props) {
  const trigger = useRef<HTMLButtonElement>(null);
  const openingDialog = useRef(false);

  function select(action: IdeaAction['action'] | 'view') {
    if (!trigger.current) return;
    openingDialog.current = true;
    onSelect({ action, returnFocus: trigger.current });
  }

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) openingDialog.current = false;
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          ref={trigger}
          variant='ghost'
          size='icon'
          aria-label={`Actions for request #${request.id}`}
          title={`Actions for request #${request.id}`}
        >
          <MoreHorizontal className='size-4' aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align='end'
        onCloseAutoFocus={(event) => {
          if (openingDialog.current) event.preventDefault();
        }}
      >
        <DropdownMenuItem onSelect={() => select('view')}>View details</DropdownMenuItem>
        {!request.withdrawn_at && <DropdownMenuSeparator />}
        {(request.withdrawn_at ? [] : allowedIdeaActions[request.status]).map((action) => (
          <DropdownMenuItem key={action} onSelect={() => select(action)}>
            {ideaActionLabels[action]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
